import React from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { cva, type VariantProps } from 'class-variance-authority';
import { clsx } from 'clsx';

const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-full font-semibold tracking-wide transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        solid: 'border-transparent',
        outline: 'border-2 bg-transparent',
        ghost: 'bg-transparent border-transparent',
      },
      colorScheme: {
        primary: '',
        secondary: '',
        inverted: '',
        dark: '',
      },
      size: {
        sm: 'px-4 py-2 text-sm',
        md: 'px-6 py-3 text-base',
        lg: 'px-8 py-4 text-lg',
      },
    },
    compoundVariants: [
      // Solid Variants
      { variant: 'solid', colorScheme: 'primary', className: 'bg-[hsl(var(--navy))] text-white hover:bg-[hsl(var(--navy-dark))] focus:ring-[hsl(var(--navy))]' },
      { variant: 'solid', colorScheme: 'secondary', className: 'bg-[hsl(var(--bronze-gold))] text-white hover:opacity-90 focus:ring-[hsl(var(--bronze-gold))]' },
      { variant: 'solid', colorScheme: 'inverted', className: 'bg-white text-[hsl(var(--navy))] hover:bg-gray-200 focus:ring-white' },
      { variant: 'solid', colorScheme: 'dark', className: 'bg-gray-800 text-white hover:bg-gray-700 focus:ring-gray-800' },

      // Outline Variants
      { variant: 'outline', colorScheme: 'primary', className: 'border-[hsl(var(--navy))] text-[hsl(var(--navy))] hover:bg-[hsl(var(--navy))] hover:text-white focus:ring-[hsl(var(--navy))]' },
      { variant: 'outline', colorScheme: 'secondary', className: 'border-[hsl(var(--bronze-gold))] text-[hsl(var(--bronze-gold))] hover:bg-[hsl(var(--bronze-gold))] hover:text-white focus:ring-[hsl(var(--bronze-gold))]' },
      { variant: 'outline', colorScheme: 'inverted', className: 'border-white text-white hover:bg-white hover:text-[hsl(var(--navy))] focus:ring-white' },
      { variant: 'outline', colorScheme: 'dark', className: 'border-gray-800 text-gray-800 hover:bg-gray-800 hover:text-white focus:ring-gray-800' },

      // Ghost Variants
      { variant: 'ghost', colorScheme: 'primary', className: 'text-[hsl(var(--navy))] hover:bg-[hsl(var(--navy))]/10 focus:ring-[hsl(var(--navy))]' },
      { variant: 'ghost', colorScheme: 'inverted', className: 'text-white hover:bg-white/10 focus:ring-white' },
      { variant: 'ghost', colorScheme: 'dark', className: 'text-gray-800 hover:bg-gray-100 focus:ring-gray-800' },
    ],
    defaultVariants: {
      variant: 'solid',
      colorScheme: 'primary',
      size: 'md',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, colorScheme, ...props }, ref) => {
    return (
      <button
        className={clsx(buttonVariants({ variant, size, colorScheme, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export interface LinkButtonProps
  extends Omit<LinkProps, 'to'>,
    VariantProps<typeof buttonVariants> {
  href?: string;
  to?: string;
}

const LinkButton = React.forwardRef<HTMLAnchorElement, LinkButtonProps>(
  ({ className, variant, size, colorScheme, href, to, ...props }, ref) => {
    const buttonClasses = clsx(buttonVariants({ variant, size, colorScheme, className }));
    
    // If href is provided, it's an external link
    if (href) {
      return (
        <a
          href={href}
          className={buttonClasses}
          ref={ref}
          target="_blank"
          rel="noopener noreferrer"
          {...props}
        />
      );
    }
    
    // Otherwise, it's an internal link using react-router
    return (
      <Link
        to={to || '#'}
        className={buttonClasses}
        ref={ref}
        {...props}
      />
    );
  }
);
LinkButton.displayName = 'LinkButton';

export { Button, LinkButton };
